import { useState } from "react";
import { MessageCircle, Mail, Phone, MapPin } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import { BuyerForm, PartnerForm, GeneralForm, DemoForm } from "../components/EnquiryForms";
import { whatsappLink } from "../lib/api";

const TABS = [
    { id: "buyer", label: "Investor / Buyer" },
    { id: "partner", label: "Developer / Partner" },
    { id: "demo", label: "Technology / White Label" },
    { id: "general", label: "General" },
];

const ContactRow = ({ icon: Icon, label, value, href, testid }) => (
    <div className="flex items-start gap-4 border-b border-ink/10 py-5" data-testid={testid}>
        <Icon className="mt-0.5 h-4 w-4 text-gold" strokeWidth={1.5} />
        <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-ink/45">{label}</p>
            {href ? (
                <a
                    href={href}
                    className="link-underline mt-1 inline-block text-sm font-light text-ink/75 transition-colors duration-300 hover:text-ink"
                >
                    {value}
                </a>
            ) : (
                <p className="mt-1 text-sm font-light text-ink/75">{value}</p>
            )}
        </div>
    </div>
);

const Contact = () => {
    const [tab, setTab] = useState("buyer");

    return (
        <main data-testid="contact-page">
            <section className="bg-charcoal pb-20 pt-40 text-ivory md:pb-28 md:pt-52">
                <div className="mx-auto max-w-[1400px] px-6 md:px-12">
                    <Reveal>
                        <Overline>Contact</Overline>
                        <h1 className="mt-6 max-w-3xl font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl">
                            Speak With <span className="italic text-gold">LUXATI.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-ivory/70">
                            Whether you are evaluating an opportunity, representing a project, considering
                            the platform, or simply exploring — the conversation starts here.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto grid max-w-[1400px] gap-16 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32">
                <div className="md:col-span-4">
                    <Reveal>
                        <Overline>Direct</Overline>
                        <a
                            data-testid="contact-whatsapp"
                            href={whatsappLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group mt-8 flex items-center gap-4 border border-ink/15 p-6 transition-colors duration-500 hover:border-gold"
                        >
                            <span className="flex h-11 w-11 items-center justify-center bg-ink text-ivory transition-colors duration-500 group-hover:bg-gold group-hover:text-ink">
                                <MessageCircle className="h-5 w-5" strokeWidth={1.5} />
                            </span>
                            <span>
                                <span className="block font-serif text-xl font-medium tracking-tight text-ink">WhatsApp</span>
                                <span className="block text-xs font-light text-ink/50">
                                    Chat with the LUXATI team — +91 78787 87141
                                </span>
                            </span>
                        </a>
                        <div className="mt-10">
                            <ContactRow icon={Mail} label="Business Email" value="info@luxati.com" href="mailto:info@luxati.com" testid="contact-email" />
                            <ContactRow icon={Phone} label="Phone / WhatsApp" value="+91 78787 87141" href="tel:+917878787141" testid="contact-phone" />
                            <ContactRow icon={MapPin} label="Location" value="Noida, India" testid="contact-address" />
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.15} className="md:col-span-7 md:col-start-6">
                    <div className="flex flex-wrap gap-3" data-testid="contact-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`contact-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`border px-6 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-500 ${
                                    tab === t.id
                                        ? "border-ink bg-ink text-ivory"
                                        : "border-ink/20 text-ink/60 hover:border-gold hover:text-ink"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="mt-12">
                        {tab === "buyer" && <BuyerForm testid="contact-buyer-form" />}
                        {tab === "partner" && <PartnerForm testid="contact-partner-form" />}
                        {tab === "demo" && <DemoForm testid="contact-demo-form" />}
                        {tab === "general" && <GeneralForm testid="contact-general-form" />}
                    </div>
                </Reveal>
            </section>
        </main>
    );
};

export default Contact;
